import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import { publicApi } from '../utils/api';
import { IMG } from '../lib/images';
import SEOHead from '../components/SEOHead';
import AnswerFirstContent from '../components/AnswerFirstContent';

const AlternativesPage = () => {
  const { slug } = useParams();
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let active = true;
    const load = async () => {
      setLoading(true);
      setError('');
      try {
        const response = await publicApi.getAlternativesPageBySlug(slug);
        if (!active) {
          return;
        }
        setPage(response.data);
      } catch {
        if (active) {
          setError('This alternatives page is not available yet.');
        }
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    };

    load();
    return () => {
      active = false;
    };
  }, [slug]);

  const compareUrl = useMemo(() => {
    const cardSlugs = (page?.alternative_cards || []).slice(0, 2).map((tool) => tool.slug);
    const slugs = [slug, ...cardSlugs].filter(Boolean);
    return `/compare?tools=${slugs.join(',')}`;
  }, [slug, page]);

  const lastVerified = useMemo(() => {
    const value = page?.updated_at || page?.date_modified || page?.created_at;
    if (!value) {
      return 'Recently';
    }
    const date = new Date(value);
    return Number.isNaN(date.getTime())
      ? 'Recently'
      : date.toLocaleString('en-US', { month: 'long', year: 'numeric' });
  }, [page?.updated_at, page?.date_modified, page?.created_at]);

  if (loading) {
    return (
      <div className="min-h-screen pt-24 flex items-center justify-center" style={{ background: 'var(--bg-base)' }}>
        <div className="text-text-secondary">Loading alternatives...</div>
      </div>
    );
  }

  if (!page || error) {
    return (
      <div className="min-h-screen pt-24 page-bg-shell page-bg-robot">
        <section className="container mx-auto px-6 pb-16 max-w-4xl">
          <h1 className="text-3xl font-black mb-3" style={{ color: 'var(--text-primary)', fontFamily: 'Unbounded, sans-serif' }}>
            Alternatives
          </h1>
          <p style={{ color: 'var(--text-secondary)' }}>{error || 'This page is currently unavailable.'}</p>
          <Link to={`/tools/${slug}`} className="inline-flex items-center mt-6 text-cyber-yellow hover:underline">
            <ArrowLeft size={16} className="mr-2" /> Back to tool
          </Link>
        </section>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-24 page-bg-shell page-bg-robot" data-testid="alternatives-page">
      <SEOHead
        pageType="alternatives"
        title={page.meta_title || page.h1}
        description={page.meta_description || page.intro || ''}
        slug={`alternatives/${slug}`}
        faqs={page.faq || []}
        comparisons={{ rows: page.comparison_rows || [] }}
        dateModified={page.updated_at || page.date_modified || page.created_at}
        author={page.author || 'Wowrolla Editorial'}
        category="alternatives"
      />

      {/* Image hero banner */}
      <section
        className="relative overflow-hidden"
        style={{
          backgroundImage: `linear-gradient(rgba(8,8,18,0.74), rgba(8,8,18,0.95)), url(${IMG.hero.alternatives})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <div className="absolute inset-0 prime-aurora-bg" />
        <div className="container mx-auto px-6 py-12 relative z-10">
          <Link to={`/tools/${slug}`} className="inline-flex items-center mb-6 text-text-secondary hover:text-cyber-yellow transition-colors">
            <ArrowLeft size={16} className="mr-2" /> Back to Tool
          </Link>
          <h1 className="text-3xl sm:text-4xl font-black text-white mb-2" style={{ fontFamily: 'Unbounded, sans-serif' }}>
            {page.h1}
          </h1>
          <p className="text-xs mb-2" style={{ color: 'var(--text-muted)' }}>
            Last verified: {lastVerified} · Updated when tools change pricing or features
          </p>
          <p className="text-text-secondary max-w-3xl">{page.intro}</p>
          <Link to={compareUrl} className="btn-primary mt-5 inline-block">Compare top alternatives</Link>
        </div>
      </section>

      <section className="container mx-auto px-6 pb-16">
        <AnswerFirstContent
          pageType="alternatives"
          quickAnswer={page.quick_answer || `The best ${page.base_tool?.name || ''} alternative for most teams is ${(page.alternative_cards || [])[0]?.name || 'the top ranked option'} based on pricing clarity, migration effort, and workflow fit.`}
          lastVerified={lastVerified}
          stats={[
            { value: `$${page.base_tool?.annual_estimate || page.base_tool?.avg_price || '0'}`, label: 'Current annual spend estimate' },
            { value: `${(page.alternative_cards || []).length}`, label: 'Alternatives compared' },
            { value: '2 days', label: 'Typical migration window' },
          ]}
          comparisons={{
            headers: ['Alternative', 'Feature highlights', 'Price', 'Commission'],
            rows: (page.comparison_rows || []).map((row) => [row.name, row.feature_highlights, row.price, row.commission_transparency]),
          }}
          faqs={page.faq || []}
        >
        <div className="terminal-grid p-5 mb-6 mt-8" data-testid="alternatives-frustration-validation">
          <p className="text-xs uppercase tracking-[0.16em] font-mono text-cyber-yellow mb-2">If this sounds familiar</p>
          <p style={{ color: 'var(--text-secondary)' }}>
            Teams usually visit alternatives pages when costs are rising, onboarding is dragging, or leadership wants higher confidence before renewal.
            This page is built to validate those concerns and reduce switching regret.
          </p>
        </div>

        <div id="switch-cost" className="terminal-grid p-6 mb-6" data-testid="alternatives-switch-cost-anchor">
          <h2 className="text-2xl font-bold mb-3" style={{ color: 'var(--text-primary)', fontFamily: 'Unbounded, sans-serif' }}>
            Before switching: calculate transition cost
          </h2>
          <p style={{ color: 'var(--text-secondary)' }}>
            Direct pricing is only one part of the decision. Include migration time, retraining overhead, and integration rework when comparing alternatives.
          </p>
        </div>

        <div className="terminal-grid p-6 mb-6">
          <h2 className="text-2xl font-bold mb-4" style={{ color: 'var(--text-primary)', fontFamily: 'Unbounded, sans-serif' }}>
            Why people switch from {page.base_tool?.name}
          </h2>
          <ul className="space-y-3" style={{ color: 'var(--text-secondary)' }}>
            {(page.switch_reasons || []).map((reason) => (
              <li key={reason} className="flex items-start gap-2">
                <span style={{ color: 'var(--accent-primary)' }}>-</span>
                <span>{reason}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {(page.alternative_cards || []).map((tool) => (
            <div key={tool.tool_id || tool.slug} className="terminal-grid p-5">
              <h2 className="text-lg font-semibold mb-1" style={{ color: 'var(--text-primary)' }}>{tool.name}</h2>
              <p className="text-sm mb-3" style={{ color: 'var(--text-secondary)' }}>{tool.tagline}</p>
              <p className="text-sm mb-4" style={{ color: 'var(--text-secondary)' }}>{tool.paragraph}</p>
              <div className="flex gap-2">
                <Link to={`/tools/${tool.slug}`} className="btn-secondary text-sm">View</Link>
                <Link to={`/compare?tools=${slug},${tool.slug}`} className="btn-primary text-sm">Compare</Link>
              </div>
            </div>
          ))}
        </div>

        <div className="terminal-grid p-6 mt-6 overflow-x-auto">
          <h2 className="text-2xl font-bold mb-4" style={{ color: 'var(--text-primary)', fontFamily: 'Unbounded, sans-serif' }}>
            Feature and pricing comparison
          </h2>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left border-b" style={{ borderColor: 'var(--border-subtle)', color: 'var(--text-muted)' }}>
                <th className="py-3 pr-4">Alternative</th>
                <th className="py-3 pr-4">Feature highlights</th>
                <th className="py-3 pr-4">Price</th>
                <th className="py-3">Commission transparency</th>
              </tr>
            </thead>
            <tbody>
              {(page.comparison_rows || []).map((row) => (
                <tr key={row.tool_id || row.slug} className="border-b" style={{ borderColor: 'var(--border-subtle)' }}>
                  <td className="py-3 pr-4 text-white">
                    <Link to={`/tools/${row.slug}`} className="hover:text-cyber-yellow transition-colors">{row.name}</Link>
                  </td>
                  <td className="py-3 pr-4" style={{ color: 'var(--text-secondary)' }}>{row.feature_highlights}</td>
                  <td className="py-3 pr-4" style={{ color: 'var(--text-secondary)' }}>{row.price}</td>
                  <td className="py-3" style={{ color: 'var(--text-secondary)' }}>{row.commission_transparency}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="terminal-grid p-6 mt-6">
          <h2 className="text-2xl font-bold mb-3" style={{ color: 'var(--text-primary)', fontFamily: 'Unbounded, sans-serif' }}>
            Which alternative is right for you?
          </h2>
          <p style={{ color: 'var(--text-secondary)' }} className="mb-4">
            {page.finder_cta || 'Use the conversational finder to match tools to your workflow, budget, and team constraints.'}
          </p>
          <Link to="/find" className="inline-flex items-center text-cyber-yellow hover:underline">
            Open tool finder <ArrowRight size={16} className="ml-1" />
          </Link>
        </div>

        <div className="terminal-grid p-6 mt-6" data-testid="alternatives-migration-confidence">
          <h2 className="text-xl font-bold mb-3" style={{ color: 'var(--text-primary)', fontFamily: 'Unbounded, sans-serif' }}>
            Migration confidence checklist
          </h2>
          <ul className="space-y-2 text-sm" style={{ color: 'var(--text-secondary)' }}>
            <li>- Validate one mission-critical workflow in a pilot first.</li>
            <li>- Confirm API/integration parity before broad rollout.</li>
            <li>- Define clear rollback conditions before switching fully.</li>
          </ul>
        </div>

        </AnswerFirstContent>

        <div className="terminal-grid p-6 mt-6" data-testid="alternatives-consulting-bridge">
          <h2 className="text-xl font-bold mb-3" style={{ color: 'var(--text-primary)', fontFamily: 'Unbounded, sans-serif' }}>
            Still unsure which path to take?
          </h2>
          <p style={{ color: 'var(--text-secondary)' }} className="mb-3">
            If switching risk is high for your team, use a consulting session to pressure-test your shortlist before committing budget.
          </p>
          <Link to="/consulting" className="inline-flex items-center text-cyber-yellow hover:underline">
            Book consulting <ArrowRight size={16} className="ml-1" />
          </Link>
        </div>

        {(page.related_pages || []).length > 0 && (
          <div className="mt-6">
            <h2 className="text-xl font-bold mb-3" style={{ color: 'var(--text-primary)', fontFamily: 'Unbounded, sans-serif' }}>
              Related alternatives pages
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
              {page.related_pages.map((related) => (
                <Link key={related.slug} to={`/alternatives/${related.slug}`} className="terminal-grid p-4 hover:border-cyber-yellow/30 transition-colors">
                  <p className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>{related.h1}</p>
                </Link>
              ))}
            </div>
          </div>
        )}
      </section>
    </div>
  );
};

export default AlternativesPage;

